import {
  createContext,
  type RefObject,
  useContext,
  useEffect,
  useLayoutEffect,
  useSyncExternalStore,
} from 'react'
import type { AnyRouteMatch } from '@tanstack/react-router'
import {
  type AuthSession,
  type AuthSessionSnapshot,
  type AuthSessionStatus,
  authSession,
} from './auth-session'
import { onWake } from './recovery-loop'

/** The auth session the notices follow: the app's own, or one a test provides */
export const AuthSessionContext = createContext<AuthSession>(authSession)

export function useAuthSession(): AuthSessionSnapshot & { session: AuthSession } {
  const session = useContext(AuthSessionContext)
  const snapshot = useSyncExternalStore(session.subscribe, session.getSnapshot)
  return { ...snapshot, session }
}

/**
 * While the ended notice stands no request asks for a token, so a container shown again, or a tab
 * shown again or back online, asks resumeIfSignedIn: nothing listens for the storage event. A
 * layout effect, so a container that comes back never paints the notice first.
 */
export function useResumeOnSignIn(session: AuthSession, status: AuthSessionStatus): void {
  // event listener: the tab shown again, or back online, while the notice stands
  useLayoutEffect(() => {
    if (status !== 'session-ended') return
    session.resumeIfSignedIn()
    return onWake(session.resumeIfSignedIn)
  }, [session, status])
}

/** What useRoutesAuthSession needs of the invoicing router: its routes' status, and a reload */
export type RouterHolder = {
  router: {
    state: { matches: ReadonlyArray<Pick<AnyRouteMatch, 'status'>> }
    invalidate: () => Promise<void>
  }
}

/**
 * The status for the invoicing routes, which reloads the routes that failed during a connection
 * problem once a token is acquired again. The shell's boundary cannot: a failed root route renders
 * its error outside the shell.
 */
export function useRoutesAuthSession(routerRef: RefObject<RouterHolder | null>): AuthSessionStatus {
  const { status, recoveries, session } = useAuthSession()
  useResumeOnSignIn(session, status)

  // network state sync: reload what failed while no token could be acquired
  useEffect(() => {
    const router = routerRef.current?.router
    if (recoveries === 0 || !router) return
    if (router.state.matches.some((match) => match.status === 'error')) void router.invalidate()
  }, [recoveries, routerRef])

  return status
}
